import {
  Badge, Card, Tab, TabGroup, TabList, TabPanel, TabPanels,
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { t } from '../lib/i18n';
import type { LineCard, VendorScorecard } from '../api/types';
import IconButton from './IconButton';
import Modal from './Modal';
import { CloseIcon } from './icons';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', minimumFractionDigits: 2,
});
const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1,
});

interface Props {
  vendor: VendorScorecard;
  lineCards: LineCard[];
  showCost: boolean;
  onClose: () => void;
}

export default function VendorDetailModal({ vendor, lineCards, showCost, onClose }: Props) {
  const cards = lineCards.filter((c) => c.sid === vendor.sid && c.sup_id === vendor.sup_id);

  const fillBadge = (fr: number | null) => {
    if (fr == null) return <span className="text-tremor-content">—</span>;
    const color = fr >= 0.95 ? 'green' : fr >= 0.80 ? 'amber' : 'red';
    return <Badge color={color}>{fmtPct.format(fr)}</Badge>;
  };

  return (
    <Modal
      onClose={onClose}
      ariaLabel={`${vendor.name} (${vendor.sup_id})`}
      role="dialog"
      panelClassName="max-w-5xl my-8 p-6"
    >
      <div className="flex items-start gap-4">
        <Title>
          {vendor.name}
          <span className="text-tremor-content text-base font-normal ml-2">
            ({vendor.sup_id})
          </span>
        </Title>
        <IconButton label={t.common.close} onClick={onClose} className="ml-auto">
          <CloseIcon />
        </IconButton>
      </div>

      <Card className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-tremor-content text-xs">{t.vendors.cols.poCount}</div>
          <div className="font-semibold">{vendor.po_count}</div>
        </div>
        <div>
          <div className="text-tremor-content text-xs">{t.vendors.cols.fillRate}</div>
          <div>{fillBadge(vendor.fill_rate)}</div>
        </div>
        <div>
          <div className="text-tremor-content text-xs">{t.vendors.cols.leadTime}</div>
          <div className="font-semibold">
            {vendor.avg_lead_time_days == null ? '—' : vendor.avg_lead_time_days.toFixed(1)}
          </div>
        </div>
        {showCost && (
          <div>
            <div className="text-tremor-content text-xs">{t.vendors.cols.costTrend}</div>
            <div className="font-semibold">
              {vendor.cost_trend_pct == null ? '—' : fmtPct.format(vendor.cost_trend_pct)}
            </div>
          </div>
        )}
      </Card>

      <TabGroup className="mt-4">
        <TabList>
          <Tab>{t.vendors.modal.tabPOs} ({vendor.recent_pos.length})</Tab>
          <Tab>{t.vendors.modal.tabCards} ({cards.length})</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {vendor.recent_pos.length === 0 ? (
              <Text className="mt-3">{t.vendors.emptyPOs}</Text>
            ) : (
              <Card className="p-0 overflow-hidden mt-3">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t.vendors.cols.ponum}</TableHeaderCell>
                      <TableHeaderCell>{t.vendors.cols.podate}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.vendors.cols.lines}</TableHeaderCell>
                      {showCost && (
                        <TableHeaderCell className="text-right">{t.vendors.cols.ordered}</TableHeaderCell>
                      )}
                      {showCost && (
                        <TableHeaderCell className="text-right">{t.vendors.cols.received}</TableHeaderCell>
                      )}
                      <TableHeaderCell className="text-right">{t.vendors.cols.fillRate}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.vendors.cols.leadTime}</TableHeaderCell>
                      <TableHeaderCell>{t.vendors.cols.status}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendor.recent_pos.map((p) => (
                      <TableRow key={p.ponum}>
                        <TableCell className="font-mono text-xs">{p.ponum}</TableCell>
                        <TableCell>{p.podate}</TableCell>
                        <TableCell className="text-right">{p.lines}</TableCell>
                        {showCost && (
                          <TableCell className="text-right">{p.ordered_mxn !== undefined ? fmtMxn.format(p.ordered_mxn) : '—'}</TableCell>
                        )}
                        {showCost && (
                          <TableCell className="text-right">{p.received_mxn !== undefined ? fmtMxn.format(p.received_mxn) : '—'}</TableCell>
                        )}
                        <TableCell className="text-right">{fillBadge(p.fill_rate)}</TableCell>
                        <TableCell className="text-right">{p.lead_time_days == null ? '—' : `${p.lead_time_days}d`}</TableCell>
                        <TableCell>{p.status || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabPanel>
          <TabPanel>
            {cards.length === 0 ? (
              <Text className="mt-3">{t.vendors.emptyLineCards}</Text>
            ) : (
              <Card className="p-0 overflow-hidden mt-3">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>{t.vendors.cols.line}</TableHeaderCell>
                      <TableHeaderCell>{t.vendors.cols.altLine}</TableHeaderCell>
                      <TableHeaderCell>{t.vendors.cols.xline}</TableHeaderCell>
                      <TableHeaderCell className="text-right">{t.vendors.cols.lev}</TableHeaderCell>
                      {showCost && (
                        <TableHeaderCell className="text-right">{t.vendors.cols.disc}</TableHeaderCell>
                      )}
                      <TableHeaderCell>{t.vendors.cols.curr}</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cards.map((c) => (
                      <TableRow key={`${c.sid}-${c.line}`}>
                        <TableCell className="font-mono">{c.line}</TableCell>
                        <TableCell>{c.alt_line || '—'}</TableCell>
                        <TableCell>{c.xline || '—'}</TableCell>
                        <TableCell className="text-right">{c.lev}</TableCell>
                        {showCost && (
                          <TableCell className="text-right">{c.disc === undefined ? '—' : fmtPct.format(c.disc)}</TableCell>
                        )}
                        <TableCell>{c.curr || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Modal>
  );
}
